import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useBoardChartData } from '../../hooks/useBoardChartData';
import { BaseChartProps } from './types';
import { CHART_COLORS, CHART_STYLES } from './chartTheme';
import { useSetChartMetadata } from './ChartContext';
import { format, parseISO } from 'date-fns';
import {
    ResponsiveContainer,
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ReferenceLine,
} from 'recharts';

export const AdSpendPercentChart: React.FC<BaseChartProps> = () => {
    const { t } = useTranslation();
    const { finance } = useBoardChartData();

    const chartData = useMemo(() => {
        if (!finance?.dailyBreakdown) return [];

        const sortedDates = Object.keys(finance.dailyBreakdown!).sort();

        return sortedDates.map((date) => {
            const day = finance.dailyBreakdown![date];
            const revenue = Number(day.metrics?.revenue) || 0;
            const adSpend = Number(day.metrics?.adSpend) || 0;
            const percent = revenue > 0
                ? Math.round((adSpend / revenue) * 10000) / 100
                : 0;

            return {
                date,
                label: format(parseISO(date), 'MMM d'),
                percent,
            };
        });
    }, [finance?.dailyBreakdown]);

    const isEmpty = useMemo(() => chartData.length === 0, [chartData]);

    useSetChartMetadata({ isEmpty });

    return (
        <div className="w-full h-full bg-transparent overflow-hidden flex flex-col">
            <div className="flex-1 min-h-0 p-2">
                <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                        data={chartData}
                        margin={CHART_STYLES.margin.withAxis}
                    >
                        <defs>
                            <linearGradient id="adSpendPercentGradient" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor={CHART_COLORS.accent} stopOpacity={0.3} />
                                <stop offset="95%" stopColor={CHART_COLORS.accent} stopOpacity={0.05} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid
                            vertical={false}
                            stroke={CHART_STYLES.grid.stroke}
                            strokeDasharray={CHART_STYLES.grid.strokeDasharray}
                            strokeWidth={CHART_STYLES.grid.strokeWidth}
                            opacity={CHART_STYLES.grid.opacity}
                        />
                        <XAxis
                            dataKey="label"
                            axisLine={CHART_STYLES.axis.axisLine}
                            tickLine={CHART_STYLES.axis.tickLine}
                            tick={CHART_STYLES.axis.tick}
                            tickMargin={CHART_STYLES.axis.tickMargin}
                        />
                        <YAxis
                            axisLine={CHART_STYLES.axis.axisLine}
                            tickLine={CHART_STYLES.axis.tickLine}
                            tick={CHART_STYLES.axis.tick}
                            tickFormatter={(value: number) => `${value}%`}
                        />
                        <Tooltip
                            contentStyle={CHART_STYLES.tooltip.contentStyle}
                            cursor={CHART_STYLES.tooltip.cursor}
                            formatter={(value: number) => [
                                `${value}%`,
                                t('charts.adSpendPercent', 'Ad Spend %'),
                            ]}
                        />
                        <ReferenceLine
                            y={30}
                            stroke={CHART_COLORS.danger}
                            strokeDasharray="4 4"
                            label={{
                                value: '30%',
                                position: 'right',
                                fill: CHART_COLORS.danger,
                                fontSize: 10,
                            }}
                        />
                        <Area
                            type="monotone"
                            dataKey="percent"
                            stroke={CHART_COLORS.accent}
                            strokeWidth={CHART_STYLES.line.strokeWidth}
                            fill="url(#adSpendPercentGradient)"
                            dot={CHART_STYLES.line.dot}
                            activeDot={{ ...CHART_STYLES.line.activeDot, fill: CHART_COLORS.accent }}
                        />
                    </AreaChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};
